import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import SupportRequestType from '@/models/common/SupportRequestType';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import { formatDateLocale } from '@/helpers/dateHelper';
import styles from './ReduceSupportRequestAmountDialog.module.css';

interface ReduceSupportRequestAmountBody {
  correlationId: string;
  counter: number | null;
  newAmount: number;
  applyToOthers: boolean;
  comment?: string;
}

interface ReduceSupportRequestAmountDialogProps {
  open: boolean;
  onClose: () => void;
  request: SupportRequestDtoIn | null;
  onSuccess: () => void;
}

/**
 * Whether the request has at least one month a coordinator may still lower the amount of (HAT-1398).
 * The server answers (SupportRequestDtoOut.CanReduceAmount) - the same rule its reduce endpoint
 * enforces, so this screen never keeps a status list of its own. Missing from an older API means
 * "not offered", which is the safe side for an action that changes money.
 */
export const hasReducibleMonth = (request: SupportRequestDtoIn): boolean => (request.canReduceAmount ?? []).some(Boolean);

const ReduceSupportRequestAmountDialog = ({ open, onClose, request, onSuccess }: ReduceSupportRequestAmountDialogProps) => {
  const { t, i18n } = useTranslation();
  const { currentFund } = useFundContext();

  const isMonthly = request?.type === SupportRequestType.Monthly;
  const isLifecycle1 = request?.isInInitialStatusProcess ?? false;
  // A monthly request the office already approved is reduced month by month; in the office's
  // initial-approval process, and for a one-time request, one decision covers every month.
  const isMonthlyL2 = isMonthly && !isLifecycle1;

  const [selectedCounter, setSelectedCounter] = useState<number | null>(null);
  const [applyToOthers, setApplyToOthers] = useState(false);
  const [newAmount, setNewAmount] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSucceeded, setIsSucceeded] = useState(false);

  const { mutate: reduceAmount, isPending: isSubmitting } = usePutAuthApi<void, object, ReduceSupportRequestAmountBody>(
    `funds/${currentFund?.id}/support-requests/reduce-amount`
  );

  useEffect(() => {
    if (open && request) {
      setError(null);
      setApplyToOthers(false);
      setIsSucceeded(false);
      setComment('');
      setNewAmount('');
      const firstReducibleIdx = (request.canReduceAmount ?? []).findIndex(Boolean);
      setSelectedCounter(
        request.type === SupportRequestType.Monthly && !request.isInInitialStatusProcess && firstReducibleIdx >= 0 ? firstReducibleIdx + 1 : null
      );
    }
  }, [open, request]);

  if (!request) return null;

  const approvedAmountOf = (idx: number): number => request.approvedAmounts?.[idx] ?? request.amount;

  const canReduceMonth = (idx: number): boolean => (isMonthlyL2 ? request.canReduceAmount?.[idx] === true : request.canReduceAmount?.[0] === true);

  // The months a submit would touch, and so the ceiling the new amount has to stay under: the
  // server refuses the whole change if any of them would go up.
  const affectedIndexes = (): number[] => {
    const all = request.requestedForDates.map((_, idx) => idx);
    if (!isMonthlyL2) return all.filter(canReduceMonth);
    if (selectedCounter === null) return [];
    return all.filter((idx) => canReduceMonth(idx) && (applyToOthers ? idx + 1 >= selectedCounter : idx + 1 === selectedCounter));
  };

  const affected = affectedIndexes();
  const currentAmount = affected.length > 0 ? Math.min(...affected.map(approvedAmountOf)) : request.amount;
  const numericAmount = Number(newAmount);

  const formatCurrency = (amount: number) => amount.toLocaleString(i18n.language, { style: 'currency', currency: 'ILS' });

  const validate = (): string | null => {
    if (newAmount.trim() === '' || !Number.isFinite(numericAmount) || numericAmount <= 0) {
      return t('reduceSupportRequestAmount.invalidAmount');
    }
    if (numericAmount >= currentAmount) {
      return t('reduceSupportRequestAmount.mustBeLower', { amount: formatCurrency(currentAmount) });
    }
    return null;
  };

  const handleSubmit = () => {
    if (!currentFund?.id) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);

    const body: ReduceSupportRequestAmountBody = {
      correlationId: request.correlationId,
      counter: isMonthlyL2 ? selectedCounter : null,
      newAmount: numericAmount,
      applyToOthers: isMonthlyL2 ? applyToOthers : false,
      comment: comment.trim() || undefined,
    };

    reduceAmount(
      { body },
      {
        onSuccess: () => {
          setIsSucceeded(true);
          onSuccess();
        },
        onError: (err) => {
          setError(t('reduceSupportRequestAmount.error') + ': ' + err.message);
        },
      }
    );
  };

  const handleClose = () => {
    setError(null);
    setIsSucceeded(false);
    onClose();
  };

  const isSubmitDisabled = isSubmitting || affected.length === 0 || newAmount.trim() === '';

  const renderMonthSelector = () => (
    <div className={styles.section}>
      <label className={styles.sectionLabel}>{t('reduceSupportRequestAmount.selectMonth')}:</label>
      <div className={styles.monthList}>
        {request.requestedForDates.map((date, idx) => {
          const counter = idx + 1;
          const reducible = canReduceMonth(idx);
          return (
            <label key={counter} className={`${styles.radioOption} ${!reducible ? styles.disabledOption : ''}`}>
              <input
                type="radio"
                name="month"
                value={counter}
                checked={selectedCounter === counter}
                onChange={() => setSelectedCounter(counter)}
                disabled={!reducible}
              />
              <span>
                {formatDateLocale(date)} - {formatCurrency(approvedAmountOf(idx))}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );

  const renderScopeSelector = () => (
    <div className={styles.section}>
      <div className={styles.radioGroup}>
        <label className={styles.radioOption}>
          <input type="radio" name="scope" checked={!applyToOthers} onChange={() => setApplyToOthers(false)} />
          <span>{t('reduceSupportRequestAmount.thisMonthOnly')}</span>
        </label>
        <label className={styles.radioOption}>
          <input type="radio" name="scope" checked={applyToOthers} onChange={() => setApplyToOthers(true)} />
          <span>{t('reduceSupportRequestAmount.fromThisMonthOnward')}</span>
        </label>
      </div>
    </div>
  );

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) handleClose();
      }}
      title={t('reduceSupportRequestAmount.title')}
    >
      <div className={styles.content}>
        {isSucceeded ? (
          <div className={styles.successContent}>
            <p className={styles.successMessage}>{t('reduceSupportRequestAmount.successMessage')}</p>
            <div className={styles.actions}>
              <Button variant="primary" className={styles.actionButton} onClick={handleClose}>
                {t('reduceSupportRequestAmount.close')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className={styles.confirmText}>{t('reduceSupportRequestAmount.confirmText')}</p>
            {isMonthlyL2 && (
              <>
                {renderMonthSelector()}
                {renderScopeSelector()}
              </>
            )}
            <div className={styles.section}>
              <span className={styles.currentAmount}>
                {t('reduceSupportRequestAmount.currentAmount')}: {formatCurrency(currentAmount)}
              </span>
              <label className={styles.sectionLabel} htmlFor="reduceAmountInput">
                {t('reduceSupportRequestAmount.newAmountLabel')}:
              </label>
              <Input
                id="reduceAmountInput"
                className="w-full"
                type="number"
                min={0}
                value={newAmount}
                onChange={(e) => {
                  setNewAmount(e.target.value);
                  if (error) setError(null);
                }}
              />
            </div>
            <div className={styles.section}>
              <label className={styles.sectionLabel}>{t('reduceSupportRequestAmount.commentLabel')}:</label>
              <textarea
                className={styles.commentTextarea}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('reduceSupportRequestAmount.commentPlaceholder')}
                rows={3}
              />
            </div>
            {error && <p className={styles.error}>{error}</p>}
            <div className={styles.actions}>
              <Button variant="white" className={styles.secondaryButton} onClick={handleClose} disabled={isSubmitting}>
                {t('reduceSupportRequestAmount.cancel')}
              </Button>
              <Button variant="primary" className={styles.actionButton} onClick={handleSubmit} disabled={isSubmitDisabled}>
                {isSubmitting ? t('loading') : t('reduceSupportRequestAmount.confirm')}
              </Button>
            </div>
          </>
        )}
      </div>
    </MainDialogLayout>
  );
};

export default ReduceSupportRequestAmountDialog;
